/**
 * Creates a throttled dynamic function that runs either leading or trailing
 */
const createThrottle = (delay: number, leading: boolean, trailing: boolean) => {
	// Timeout
	let timeout: NodeJS.Timeout | undefined;

	// Last enqueued function
	let enqueuedFunction: (() => void) | undefined;

	/**
	 * Runs enqueued request and treats requests aborted through AbortController
	 */
	const run = () => {
		if (enqueuedFunction) {
			enqueuedFunction();
		}
	};

	/**
	 * Cancel anything debounced and clear timeout
	 */
	const cancel = () => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = undefined;
		}
		if (enqueuedFunction) {
			enqueuedFunction = undefined;
		}
	};

	/**
	 * Starts timeout and either enqueues or runs a new call
	 */
	const enqueue = (fn: () => void): void => {
		// If no timeout - start timeout and run function if leading is true
		if (!timeout) {
			timeout = setTimeout(() => {
				run();
				timeout = undefined;
			}, delay);

			if (leading) {
				fn();
				return;
			}
		}

		// If trailing is true, store the function to run later
		if (trailing) {
			enqueuedFunction = fn;
		}
	};

	enqueue.cancel = cancel;
	return enqueue;
};

/**
 *
 * @param {function} fn function to call
 * @param {number} delay delay time in ms
 */
export function throttle(fn: () => void, delay: number): () => void {
	const throttled = createThrottle(delay, true, false);
	return () => throttled(fn);
}

export default createThrottle;
